import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'

import type { TrashedProject } from '../../api/types.gen'
import { ApiException, api } from '../../api/client'
import { formatBytes } from '../../lib/format'
import { Banner, Button, Card } from '../../ui'

type Trashed = TrashedProject

/**
 * La papelera.
 *
 * Va al pie de la lista de proyectos y sólo aparece cuando hay algo dentro: es
 * la red de un borrado, no una sección del producto. Quien acaba de borrar por
 * error aterriza aquí —el borrado devuelve a esta pantalla— y tiene el botón
 * delante sin buscarlo.
 *
 * Los días que quedan los cuenta el servidor y se enseñan tal cual. Calcularlos
 * aquí desde `deleted_at` obligaría a acertar con la zona horaria del navegador
 * para un número que decide si alguien recupera su trabajo o no.
 */
export function TrashSection() {
  const queryClient = useQueryClient()
  const [failure, setFailure] = useState<string | null>(null)

  const trash = useQuery({
    queryKey: ['projects', 'trash'],
    queryFn: () => api.get<{ data: Trashed[] }>('/projects/trash'),
  })

  const restore = useMutation({
    mutationFn: (id: string) => api.post<{ data: unknown }>(`/projects/${id}/restore`, {}),
    onSuccess: () => {
      setFailure(null)
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['tenant-usage'] })
      void queryClient.invalidateQueries({ queryKey: ['billing'] })
    },
    onError: (error) =>
      setFailure(
        error instanceof ApiException ? error.message : 'No se pudo recuperar el proyecto.',
      ),
  })

  const items = trash.data?.data ?? []

  if (items.length === 0) return null

  return (
    <section className="mt-10 flex flex-col gap-3">
      <div>
        <h2 className="flex items-center gap-2 text-subhead font-medium text-ink">
          <Trash2 size={17} strokeWidth={1.75} aria-hidden />
          Papelera
        </h2>
        <p className="mt-1 text-body text-ink-muted">
          Ya no ocupan sitio en tu plan. Se borran solos y entonces sí es definitivo.
        </p>
      </div>

      {failure !== null && (
        <Banner tone="danger" live="alert">
          {failure}
        </Banner>
      )}

      <ul className="flex flex-col gap-2">
        {items.map((project) => (
          <li key={project.id}>
            <Card className="flex flex-wrap items-center justify-between gap-4 p-4">
              <div className="min-w-0">
                <p className="truncate text-label font-medium text-ink">{project.name}</p>
                <p className="mt-0.5 text-caption text-ink-subtle" data-numeric>
                  {project.asset_count} archivos · {formatBytes(project.storage_bytes)} ·{' '}
                  {/* El último día se dice distinto: «en 0 días» no se entiende
                      y es justo cuando hay que actuar. */}
                  <span className={project.days_left <= 1 ? 'text-danger' : undefined}>
                    {project.days_left === 0
                      ? 'se borra hoy'
                      : project.days_left === 1
                        ? 'se borra mañana'
                        : `se borra en ${project.days_left} días`}
                  </span>
                </p>
              </div>

              <Button
                variant="secondary"
                size="sm"
                disabled={restore.isPending}
                onClick={() => restore.mutate(project.id)}
              >
                Recuperar
              </Button>
            </Card>
          </li>
        ))}
      </ul>
    </section>
  )
}
